import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { CloseIcon, ChevronLeftIcon } from "components/SVG";
import { useLocale } from "hooks";
import { getPDPUrl } from "utils/getPDPUrl";
import { navigateTo } from "@shared/utils.routing";
import { useSegment } from "tracking";
import type { VariantType } from "@shared/graphql.types";
import { ShadeSelectorTooltip } from "./ShadeSelectorTooltip";
import {
  colorSwatchesWrapper,
  activeShadeBorder,
  swatchWrapper,
  outOfStockIcon,
  swatchesList,
  swatchesContainer,
  swatchLink,
  swatch,
  scrollLeftButton,
  scrollRightButton
} from "./ShadeSelector.css";

export type Props = {
  variant: VariantType;
};

export const ShadeSelectorList: React.FC<Props> = ({ variant }) => {
  const [activeShade, setActiveShade] = useState<VariantType | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const locale = useLocale();
  const { track } = useSegment();

  const updateScrollButtonsVisibility = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -200, behavior: "smooth" });
      updateScrollButtonsVisibility();
    }
  };

  const scrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: 200, behavior: "smooth" });
      updateScrollButtonsVisibility();
      setShowLeftScroll(true);
    }
  };

  useEffect(() => {
    setActiveShade(variant);
  }, [variant]);

  useEffect(() => {
    updateScrollButtonsVisibility();
  }, []);

  return (
    <div className={colorSwatchesWrapper} data-testid="shade-selector-list">
      <div className={swatchesContainer} ref={containerRef}>
        {variant?.relatedVariants?.map((shade) => {
          if (!shade) return null;
          const isOutOfStock = shade?.availability?.status === "outofstock";
          const isActiveShade = activeShade?.id === shade.id;

          return (
            <div className={swatch} key={shade.subTitle}>
              <div>
                <ShadeSelectorTooltip text={shade.subTitle || ""}>
                  <div
                    role="button"
                    onClick={() => {
                      navigateTo(getPDPUrl(shade, locale));
                      track({
                        event: "Page Element Clicked",
                        properties: {
                          module_content: `Shade Selector Slider ${activeShade?.title} to ${shade.title}`,
                          module_content_url: variant?.slug || ""
                        }
                      });
                    }}
                    key={shade.id}
                    data-testid={shade.subTitle}
                    className={swatchLink}
                    tabIndex={0}
                    onKeyDown={() => navigateTo(getPDPUrl(shade, locale))}
                  >
                    <div className={swatchWrapper}>
                      {shade.shadeVisual && (
                        <Image
                          src={shade.shadeVisual}
                          alt={shade.title}
                          width="40"
                          height="40"
                          style={{
                            opacity: isOutOfStock ? 0.5 : 1
                          }}
                          className={`${swatchesList} ${
                            isActiveShade ? activeShadeBorder : ""
                          }`}
                        />
                      )}
                      {isOutOfStock && (
                        <CloseIcon width="20px" className={outOfStockIcon} />
                      )}
                    </div>
                  </div>
                </ShadeSelectorTooltip>
              </div>
            </div>
          );
        })}
      </div>
      {showLeftScroll && (
        <button type="button" className={scrollLeftButton} onClick={scrollLeft}>
          <ChevronLeftIcon width="20" />
        </button>
      )}
      {showRightScroll && (
        <button
          type="button"
          className={scrollRightButton}
          onClick={scrollRight}
        >
          <ChevronLeftIcon style={{ transform: "rotate(180deg)" }} />
        </button>
      )}
    </div>
  );
};
